import React, { useCallback, useEffect, useMemo, useState } from "react";
import Workflow from "../Workflow";
import GenerateWorkflowDialog from "./GenerateWorkflowDialog";
import { Button } from "../StyledElements";
import { RunWorkflowDialog } from "../Dialogs";
import {
  getWorkflowNodes,
  loadGeneratedWorkflowGraph,
  extractGeneratedGraph,
} from "../../services/workflowGeneration";
import { normalizeWorkflowNodes } from "../../utils/workflowNodes";
import { diagnoseGraphEdges, summarizeEdgeDiagnostics } from "../../utils/graphEdgeDiagnostics";
import { COMPACT_NODE_SIZE, estimateNodeHeight, isCompactNode, normalizeGraphHandlesFromDefinitions } from "../../utils/dynamicNodeData";
import { layoutWorkflowGraph } from "../../utils/workflowLayout";
import { MarkerType } from "@xyflow/react";
import OrthogonalEdge from "./OrthogonalEdge";

const EDGE_TYPES = { orthogonal: OrthogonalEdge };

const EDGE_OPTIONS = {
  type: "orthogonal",
  markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18 },
};

/**
 * The dynamic-node graph editor, embedded as a tab alongside the normal workflow graph tab.
 *
 * It uses the same graph definition and the same page header (Save Changes) and sidebar as the
 * prebuilt editor — only the node renderer differs. Also offers edge diagnostics and a
 * "Generate from description" overlay.
 *
 * @param {object} props
 * @param {object|null} props.graph Workflow graph ({ nodes, edges }).
 * @param {Function} props.onGraphChange Called with the edited graph on every canvas change.
 * @param {object|null} [props.generate] Generator config; omit to hide the button.
 * @param {object|null} [props.run] Run config: `{ agent }`, `{ workflow }` (open the run dialog)
 *   or `{ onRun }` (call directly).
 * @param {object} [props.style]
 */
export const DynamicWorkflowGraphTab = ({
  graph = null,
  onGraphChange = () => { },
  generate = null,
  run = null,
  style = {},
}) => {
  const [definitions, setDefinitions] = useState(null);
  const [graphVersion, setGraphVersion] = useState(0);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getWorkflowNodes()
      .then((result) => {
        if (cancelled) return;
        setDefinitions(result.success ? normalizeWorkflowNodes(result.payload) : []);
      })
      .catch(() => {
        if (!cancelled) setDefinitions([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const definitionsByType = useMemo(() => {
    const map = {};
    (definitions ?? []).forEach((definition) => {
      if (definition.type) map[definition.type] = definition;
    });
    return map;
  }, [definitions]);

  // Entry/output nodes are measured as wide bars in stored graphs; render them as small squares.
  const preparedGraph = useMemo(() => {
    if (!graph) return graph;

    const compactTypes = new Set((definitions ?? []).filter(isCompactNode).map((definition) => definition.type));

    const nodes = (graph.nodes ?? []).map((node) => {
      if (!node || !compactTypes.has(node.type)) return node;
      const { measured, style: nodeStyle, height, ...rest } = node;
      return { ...rest, width: COMPACT_NODE_SIZE, height: COMPACT_NODE_SIZE };
    });

    // Let the orthogonal edge type apply, and map legacy handle names onto the catalog's ports
    // so every edge lines up with the handles the node actually renders.
    return normalizeGraphHandlesFromDefinitions(
      { ...graph, nodes, edges: (graph.edges ?? []).map(({ type, ...edge }) => edge) },
      definitionsByType
    );
  }, [graph, definitions, definitionsByType]);

  const diagnostics = useMemo(() => {
    if (!graph || !definitions) return null;
    return diagnoseGraphEdges(graph, definitionsByType);
  }, [graph, definitions, definitionsByType]);

  const summary = useMemo(() => (diagnostics ? summarizeEdgeDiagnostics(diagnostics) : null), [diagnostics]);

  const copyDiagnostics = useCallback(() => {
    if (!summary) return;
    const text = JSON.stringify(summary, null, 2);
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      window.prompt("Copy the diagnostics JSON:", text);
    }
  }, [summary]);

  // Size + position every node: the generator's graph overlaps and has oversized boxes.
  const runAutoLayout = useCallback(
    (baseGraph) => {
      if (!baseGraph || !Array.isArray(baseGraph.nodes) || baseGraph.nodes.length === 0) return baseGraph;

      const sizeFor = (node) => {
        const definition = definitionsByType[node.type];
        if (!definition) return { width: Number(node.width) || 320, height: Number(node.height) || 160 };
        if (isCompactNode(definition)) return { width: COMPACT_NODE_SIZE, height: COMPACT_NODE_SIZE };
        return {
          width: definition.layout?.width ?? 360,
          height: estimateNodeHeight(definition, node.data ?? {}),
        };
      };

      const laidOut = layoutWorkflowGraph(baseGraph.nodes, baseGraph.edges, {
        sizeFor,
        gapX: 220,
        gapY: 90,
      });

      const nodes = laidOut.nodes.map((node) => {
        const definition = definitionsByType[node.type];
        if (!definition) return node;

        // Drop any stored size so the node measures its content; keep a sensible width.
        const { measured, height, ...rest } = node;

        return isCompactNode(definition)
          ? { ...rest, width: COMPACT_NODE_SIZE, height: COMPACT_NODE_SIZE }
          : { ...rest, width: definition.layout?.width ?? 360 };
      });

      return { ...baseGraph, nodes };
    },
    [definitionsByType]
  );

  const applyAutoLayout = useCallback(() => {
    if (!graph) return;
    const next = runAutoLayout(graph);
    if (!next || next === graph) return;
    onGraphChange(next);
    setGraphVersion((version) => version + 1);
  }, [graph, onGraphChange, runAutoLayout]);

  const applyGeneratedWorkflow = useCallback(
    async (saved, results) => {
      setShowGenerate(false);

      const inlineGraph = extractGeneratedGraph(results);
      const loadedGraph = inlineGraph ?? (await loadGeneratedWorkflowGraph(saved?.public_id))?.graph ?? null;

      if (!loadedGraph) {
        window.alert("The workflow was generated, but no graph came back, so it could not be shown here.");
        return;
      }

      onGraphChange(runAutoLayout(loadedGraph) ?? loadedGraph);
      setGraphVersion((version) => version + 1);
    },
    [onGraphChange, runAutoLayout]
  );

  // "Run Workflow" opens the same dialog (or calls the same service) as the prebuilt tab.
  const handleRunWorkflow = useCallback(() => {
    if (!run) return;
    if (typeof run.onRun === "function") {
      run.onRun();
      return;
    }
    setShowRunDialog(true);
  }, [run]);

  const hasGraph = Boolean(preparedGraph) && Array.isArray(preparedGraph.nodes) && preparedGraph.nodes.length > 0;

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: "10px", ...style }}>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <div style={{ fontSize: "13px", color: "#a0a6b1", flex: 1 }}>
          Same graph, rendered from the server's node schema. Changes save with the page's Save Changes.
        </div>

        {generate ? (
          <Button onClick={() => setShowGenerate(true)}>Generate from description</Button>
        ) : null}

        <button
          type="button"
          onClick={applyAutoLayout}
          disabled={!hasGraph}
          title="Re-position and resize the nodes so nothing overlaps"
          style={{
            padding: "5px 10px",
            fontSize: "12px",
            cursor: hasGraph ? "pointer" : "default",
            opacity: hasGraph ? 1 : 0.5,
            border: "1px solid var(--main-border-color)",
            borderRadius: "6px",
            backgroundColor: "transparent",
            color: "var(--main-font-color)",
          }}
        >
          Tidy layout
        </button>

        <button
          type="button"
          onClick={() => setShowDiagnostics((value) => !value)}
          style={{
            padding: "5px 10px",
            fontSize: "12px",
            cursor: "pointer",
            border: "1px solid var(--main-border-color)",
            borderRadius: "6px",
            backgroundColor: "transparent",
            color: "var(--main-font-color)",
          }}
        >
          {showDiagnostics ? "Hide" : "Show"} edge diagnostics
          {summary ? ` (${summary.failing}/${summary.total} failing)` : ""}
        </button>
      </div>

      {showDiagnostics && (
        <div
          style={{
            maxHeight: "30vh",
            overflow: "auto",
            padding: "10px 12px",
            border: "1px solid var(--main-border-color)",
            borderRadius: "8px",
            fontSize: "12px",
            lineHeight: 1.5,
            backgroundColor: "var(--card-bg)",
          }}
        >
          {!summary ? (
            <div>Loading diagnostics…</div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                <strong>
                  {summary.failing} of {summary.total} edge(s) failing
                </strong>
                <button
                  type="button"
                  onClick={copyDiagnostics}
                  style={{
                    padding: "2px 8px",
                    fontSize: "11px",
                    cursor: "pointer",
                    border: "1px solid var(--main-border-color)",
                    borderRadius: "5px",
                    backgroundColor: "transparent",
                    color: "var(--main-font-color)",
                  }}
                >
                  {copied ? "Copied" : "Copy JSON"}
                </button>
              </div>

              {summary.failing === 0 ? (
                <div style={{ color: "#059669" }}>Every edge matches a rendered handle.</div>
              ) : (
                summary.edges.map((edge) => (
                  <div
                    key={edge.id}
                    style={{
                      padding: "6px 8px",
                      marginBottom: "6px",
                      border: "1px solid var(--content-item-border-color)",
                      borderRadius: "6px",
                    }}
                  >
                    <div>
                      <strong>{edge.from}</strong> → <strong>{edge.to}</strong>
                    </div>
                    <div style={{ color: "#a0a6b1" }}>
                      sourceHandle: {JSON.stringify(edge.sourceHandle)} in {JSON.stringify(edge.sourceHandles)} ·
                      targetHandle: {JSON.stringify(edge.targetHandle)} in {JSON.stringify(edge.targetHandles)}
                    </div>
                    <div style={{ color: "#c0392b" }}>{edge.problems.join("; ")}</div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}

      <div style={{ position: "relative", flex: 1, minHeight: 0, border: "1px solid var(--main-border-color)", borderRadius: "8px" }}>
        {definitions === null ? (
          <div style={{ padding: "20px", fontSize: "13px", color: "#a0a6b1" }}>Loading node definitions…</div>
        ) : (
          <Workflow
            key={graphVersion}
            dynamicNodes
            workflow={preparedGraph ?? { nodes: [], edges: [] }}
            onWorkflowChange={onGraphChange}
            onRunWorkflow={handleRunWorkflow}
            edgeTypes={EDGE_TYPES}
            defaultEdgeOptions={EDGE_OPTIONS}
            skipHandleNormalization
          />
        )}
      </div>

      {showRunDialog && run?.agent ? (
        <RunWorkflowDialog
          workflowGraph={graph ?? { nodes: [], edges: [] }}
          agent={run.agent}
          show={true}
          onClose={() => setShowRunDialog(false)}
        />
      ) : null}

      {showRunDialog && run?.workflow ? (
        <RunWorkflowDialog
          workflowGraph={graph ?? { nodes: [], edges: [] }}
          workflow={run.workflow}
          show={true}
          onClose={() => setShowRunDialog(false)}
        />
      ) : null}

      {generate ? (
        <GenerateWorkflowDialog
          show={showGenerate}
          onClose={() => setShowGenerate(false)}
          onGenerated={applyGeneratedWorkflow}
          defaultName={generate.defaultName ?? ""}
          defaultDescription={generate.defaultDescription ?? ""}
          defaultPurpose={generate.defaultPurpose ?? ""}
          defaultKind={generate.kind ?? "agent"}
          clientId={generate.clientId ?? null}
        />
      ) : null}
    </div>
  );
};

export default DynamicWorkflowGraphTab;
